import { useEffect, useState } from 'react';
import axios, { AxiosRequestConfig } from 'axios';
import { Button, Col, Container, Dropdown, DropdownButton, Form, InputGroup, Row } from 'react-bootstrap';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTransLog } from '../contexts/TransLogContext';
import useToken from '../hooks/useToken';
import { PAGE_SIZE, DURATION_DISABLE } from '../models/global';
import { SapTransactionStatistics } from '../models/SapTransLog';
import { DataListTable, HeadCell } from './DataListTable';
import { SapTransDuration, SapTransLogQuery } from '../models/SapTransLogQuery';
import { AuthenticatedTemplate, useAccount, useMsal } from '@azure/msal-react';
import TransactionStatisticsModal from './TransactionStatisticsModal';
import { AppRoles } from '../auth/authConfig';
import { ConvertToUTC } from '../libs/utilities';

function TransStatisticsSearch() {
    let startTime = new Date(), endTime = new Date();
    startTime.setHours(0, 0, 0);
    endTime.setHours(23, 59, 59);
    const [webId, setWebId] = useState(null as any as string);
    const [userName, setUserName] = useState('');
    const [transName, setTransName] = useState('');
    const [appName, setAppName] = useState('');
    const [startDate, setStartDate] = useState(startTime);
    const [endDate, setEndDate] = useState(endTime);
    const [companyKey, setCompanyKey] = useState('');
    const [isSuccess, setIsSuccess] = useState(null as any as string);
    const [durationActionFirst, setDurationActionFirst] = useState(DURATION_DISABLE);
    const [durationValueFirst, setDurationValueFirst] = useState('');
    const [durationActionSec, setDurationActionSec] = useState(DURATION_DISABLE);
    const [durationValueSec, setDurationValueSec] = useState('');
    const [durationOperation, setDurationOperation] = useState('and');

    const { searchStatistics, searchStatisticsReport, searching, exporting, statistics, statisticsReport } = useTransLog();
    const [getToken] = useToken();
    const [transactionTypes, setTransactionTypes] = useState([] as string[]);
    const [apps, setApps] = useState([] as string[]);

    const [show, setShow] = useState(false);
    const { instance, accounts } = useMsal();
    const account = useAccount(accounts[0]);
    const [isAdmin, setIsAdmin] = useState(false);

    const columns: HeadCell[] = [
        { id: 'transactionType', label: 'Transaction Name' },
        { id: 'avgDuration', label: 'Avg Duration(s)', valueGetter: (row: SapTransactionStatistics) => row.avgDuration.toFixed(2) },
        { id: 'maxDuration', label: 'Max Duration(s)', valueGetter: (row: SapTransactionStatistics) => row.maxDuration.toFixed(2) },
        { id: 'minDuration', label: 'Min Duration(s)', valueGetter: (row: SapTransactionStatistics) => row.minDuration.toFixed(2) },
        { id: 'volume', label: 'Volume' },
    ];

    const openDetail = (data: SapTransactionStatistics) => {
        let request = createRequest();
        request.granuality = "day";
        request.transName = data.transactionType;
        searchStatisticsReport(request);
        setShow(true);
    };

    const handleClose = () => {
        setShow(false);
    };

    const createRequest = () => {
        var request = {
            webId: webId,
            userName: userName,
            transName: transName,
            appName: appName,
            startDate: ConvertToUTC(startDate),
            endDate: ConvertToUTC(endDate),
            pageSize: PAGE_SIZE,
            pageIndex: 0,
            companyKey: companyKey,
            isSuccess: isSuccess,
            duration: [] as SapTransDuration[],
            durationOperation: durationOperation
        } as SapTransLogQuery;

        if (durationActionFirst !== DURATION_DISABLE && durationValueFirst !== '') {
            request.duration.push({
                action: durationActionFirst,
                value: durationValueFirst
            } as SapTransDuration);
        }

        if (durationActionSec !== DURATION_DISABLE && durationValueSec !== '') {
            request.duration.push({
                action: durationActionSec,
                value: durationValueSec
            } as SapTransDuration);
        }

        return request;
    }

    // const exportToFile = async () => {
    //     var data = await downloadTransLogs(createRequest());
    //     let link = document.createElement('a');
    //     var blob = new Blob(["\uFEFF" + data], { type: 'text/csv;charset=utf-8' });
    //     link.href = window.URL.createObjectURL(blob);
    //     link.download = 'TransactionLog.csv';
    //     link.target = '_blank';
    //     link.click();
    //     window.URL.revokeObjectURL(link.href);
    // }

    const getStatistics = (e: any) => {
        e.preventDefault();
        searchStatistics(createRequest());
    }

    useEffect(() => {
        if (account) {
            const roles = (account.idTokenClaims as any).roles ? (account.idTokenClaims as any).roles as string[] : [];
            setIsAdmin(roles.some((i: any) => i === AppRoles.Administrator.toString()))
        }
    }, [account]);

    useEffect(() => {
        async function getData() {
            const config: AxiosRequestConfig = {
                method: "GET",
                url: `${import.meta.env.VITE_BACKEND_API_URL}/translog/trxtypes`,
                headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${await getToken()}` }
            };
            let response = await axios.request<string[]>(config);
            response.status === 200 && setTransactionTypes(response.data);

            config.url = `${import.meta.env.VITE_BACKEND_API_URL}/translog/apps`;
            response = await axios.request<string[]>(config);
            response.status === 200 && setApps(response.data);
        };

        getData();
    }, []);

    return (<>
        {isAdmin ?
            <AuthenticatedTemplate>
                <Container fluid className="mt-3 layout-container">
                    <Form onSubmit={getStatistics}>
                        <Row>
                            <Col md={6}>
                                <Form.Group as={Row} className="mb-3" controlId="form.userData">
                                    <Col md={4}><Form.Label>WebId:</Form.Label></Col>
                                    <Col md={8}>
                                        <Form.Control size="sm" type="number"
                                            placeholder="Input WebId here..."
                                            onChange={(e) => { setWebId(e.target.value); }} />
                                    </Col>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group as={Row} className="mb-3" controlId="form.userName">
                                    <Col md={4}><Form.Label>User Name:</Form.Label></Col>
                                    <Col md={8}>
                                        <Form.Control size="sm" type="text"
                                            placeholder="Input User Name here..."
                                            onChange={(e) => { setUserName(e.target.value); }} />
                                    </Col>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group as={Row} className="mb-3" controlId="form.startDate">
                                    <Col md={4}><Form.Label>Start Date:</Form.Label></Col>
                                    <Col md={8}>
                                        <DatePicker
                                            showTimeInput
                                            timeInputLabel="Time:"
                                            dateFormat="yyyy-MM-dd HH:mm aa"
                                            selected={startDate}
                                            showMonthDropdown
                                            showYearDropdown
                                            dropdownMode="select"
                                            onChange={(date: Date) => setStartDate(date)} />
                                    </Col>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group as={Row} className="mb-3" controlId="form.endDate">
                                    <Col md={4}><Form.Label>End Date:</Form.Label></Col>
                                    <Col md={8}>
                                        <DatePicker
                                            showTimeInput
                                            timeInputLabel="Time:"
                                            dateFormat="yyyy-MM-dd HH:mm aa"
                                            selected={endDate}
                                            showMonthDropdown
                                            showYearDropdown
                                            dropdownMode="select"
                                            onChange={(date: Date) => setEndDate(date)} />
                                    </Col>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group as={Row} className="mb-3" controlId="form.appName">
                                    <Col md={4}><Form.Label>Application:</Form.Label></Col>
                                    <Col md={8}>
                                        <select style={{ width: '100%' }} onChange={(e) => { setAppName(e.target.value); }}>
                                            <option value="">All</option>
                                            {apps.map(i => <option value={i} key={i}>{i}</option>)}
                                        </select>
                                    </Col>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group as={Row} className="mb-3" controlId="form.transName">
                                    <Col md={4}><Form.Label>Transacion Name:</Form.Label></Col>
                                    <Col md={8}>
                                        <select style={{ width: '100%' }} onChange={(e) => { setTransName(e.target.value); }}>
                                            <option value="">All</option>
                                            {transactionTypes.map(t => <option value={t} key={t}>{t}</option>)}
                                        </select>
                                    </Col>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group as={Row} className="mb-3" controlId="form.companyKey">
                                    <Col md={4}><Form.Label>Shipto#:</Form.Label></Col>
                                    <Col md={8}>
                                        <Form.Control size="sm" type="text"
                                            placeholder="Input Shipto# here..."
                                            onChange={(e) => { setCompanyKey(e.target.value); }} />
                                    </Col>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group as={Row} className="mb-3" controlId="form.isSuccess">
                                    <Col md={4}><Form.Label>Success:</Form.Label></Col>
                                    <Col md={8}>
                                        <select style={{ width: '100%' }} onChange={(e) => { setIsSuccess(e.target.value); }}>
                                            <option value="">All</option>
                                            <option value={"1"} key={"yes"}>Yes</option>
                                            <option value={"0"} key={"no"}>No</option>
                                        </select>
                                    </Col>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group as={Row} className="mb-3">
                                    <Col md={4}><Form.Label>Duration:</Form.Label></Col>
                                    <Col md={8}>
                                        <Row>
                                            <Col md={5}>
                                                <InputGroup size="sm" className="mb-3">
                                                    <DropdownButton variant="outline-secondary" title={durationActionFirst} onSelect={(key, e: any) => setDurationActionFirst(e.target.text)}>
                                                        <Dropdown.Item>{DURATION_DISABLE}</Dropdown.Item>
                                                        <Dropdown.Item>{">"}</Dropdown.Item>
                                                        <Dropdown.Item>{"<"}</Dropdown.Item>
                                                    </DropdownButton>
                                                    <Form.Control disabled={durationActionFirst === DURATION_DISABLE} type='number' id="input-group-dropdown-input-1" onChange={(e) => setDurationValueFirst(e.target.value)} />
                                                </InputGroup>
                                            </Col>
                                            <Col md={2} style={{ padding: 0 }}>
                                                <select className='mt-1' style={{ width: '100%' }} onChange={(e) => setDurationOperation(e.target.value)}>
                                                    <option value={"And"} key={"And"}>And</option>
                                                    <option value={"Or"} key={"Or"}>Or</option>
                                                </select>
                                            </Col>
                                            <Col md={5}>
                                                <InputGroup size="sm" className="mb-3">
                                                    <DropdownButton variant="outline-secondary" title={durationActionSec} onSelect={(key, e: any) => setDurationActionSec(e.target.text)}>
                                                        <Dropdown.Item>{DURATION_DISABLE}</Dropdown.Item>
                                                        <Dropdown.Item>{">"}</Dropdown.Item>
                                                        <Dropdown.Item >{"<"}</Dropdown.Item>
                                                    </DropdownButton>
                                                    <Form.Control disabled={durationActionSec === DURATION_DISABLE} type='number' id="input-group-dropdown-input-1" onChange={(e) => setDurationValueSec(e.target.value)} />
                                                </InputGroup>
                                            </Col>
                                        </Row>
                                    </Col>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="justify-content-end">
                            {/* <Col md={3}>
                    <Button className="ap-green w-100" disabled={logs.length === 0 || exporting} onClick={exportToFile}>Export to File</Button>
                </Col> */}
                            <Col md={3}>
                                <Button type="submit" disabled={searching} className="ap-green w-100">{searching ? 'Searching...' : 'Search Statistics'}</Button>
                            </Col>
                        </Row>
                    </Form >
                </Container >
                <Container fluid className="mt-3 mb-3 layout-container">
                    <DataListTable clickableRowIds={['transactionType']} headCells={columns} dataSource={statistics} onRowClick={openDetail} />
                </Container>
                <TransactionStatisticsModal show={show} handleClose={handleClose}></TransactionStatisticsModal>
            </AuthenticatedTemplate>
            : <></>}
    </>);
}

export default TransStatisticsSearch;